import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button,
  CircularProgress, Alert, Divider, Chip, Paper
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { content } from '../api/client';

const LessonList = () => {
  const { moduleId, courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([content.module(moduleId), content.course(moduleId, courseId)])
      .then(([modRes, courseRes]) => {
        setModule(modRes.data);
        setCourse(courseRes.data);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [moduleId, courseId]);

  const quizKey = `quiz_${moduleId}_${courseId}`;
  const quizResult = JSON.parse(localStorage.getItem(quizKey) || 'null');

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom className="title-wrapper">
        {module?.icon && <img src={module.icon} alt="" className="module-icon-inline" />}
        {course?.title}
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #fff 60%, #f0f4ff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Course Quiz
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete all lessons, then pass the quiz to unlock the next course.
            </Typography>
            {quizResult && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                {quizResult.passed
                  ? <CheckCircleIcon color="success" />
                  : <QuizIcon color="warning" />
                }
                <Typography variant="body2" fontWeight="bold" color={quizResult.passed ? 'success.main' : 'warning.main'}>
                  {quizResult.passed ? `Passed — ${quizResult.score}%` : `Last attempt: ${quizResult.score}% (need 70%)`}
                </Typography>
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            color={quizResult?.passed ? 'success' : 'primary'}
            startIcon={<QuizIcon />}
            onClick={() => navigate(`/module/${moduleId}/course/${courseId}/quiz`)}
            sx={{ px: 3, py: 1.5 }}
          >
            {quizResult?.passed ? 'Quiz Passed ✓' : 'Take Quiz'}
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ mb: 3 }}>
        <Chip label="Lessons" />
      </Divider>

      <Grid container spacing={4}>
        {course?.lessons.map((lesson) => (
          <Grid item xs={12} md={6} key={lesson.id}>
            <Card className={`course-card ${moduleId}`}>
              <CardContent>
                <Typography variant="overline" sx={{ opacity: 0.7 }}>
                  Lesson {lesson.id}
                </Typography>
                <Typography variant="h6">{lesson.title}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{lesson.content}</Typography>
                {lesson.pdf ? (
                  <Button
                    href={lesson.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    sx={{ mt: 2 }}
                  >
                    View PDF
                  </Button>
                ) : (
                  <Button
                    component={Link}
                    to={`/module/${moduleId}/course/${courseId}/lesson/${lesson.id}`}
                    variant="outlined"
                    sx={{ mt: 2 }}
                  >
                    Start Lesson
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box textAlign="center" mt={4}>
        <Button
          variant="contained"
          color={quizResult?.passed ? 'success' : 'primary'}
          size="large"
          startIcon={<QuizIcon />}
          onClick={() => navigate(`/module/${moduleId}/course/${courseId}/quiz`)}
          sx={{ px: 5, py: 1.5 }}
        >
          {quizResult?.passed ? 'Quiz Completed — Retake?' : 'Take Course Quiz'}
        </Button>
      </Box>
    </Box>
  );
};

export default LessonList;
